// Fetch-based SSE reader for the canonical Run event stream. We deliberately do
// NOT use EventSource: the canonical stream replays persisted events then emits an
// `eof` frame and CLOSES, which would make EventSource auto-reconnect in a loop.
// This reader honours the Client-Side Reconnection Contract — on a drop BEFORE eof
// it resumes from the last received sequence via the Last-Event-ID header + `after`.
export async function streamCanonicalEvents(apiBase, runId, { onEvent, onEof, onError, onReconnect, signal, maxRetries = 3 } = {}) {
  let lastId = -1;
  let attempts = 0;
  while (true) {
    const qs = lastId >= 0 ? `?after=${lastId}` : "";
    const url = `${apiBase}/canonical/runs/${encodeURIComponent(runId)}/events/stream${qs}`;
    const headers = { Accept: "text/event-stream" };
    if (lastId >= 0) headers["Last-Event-ID"] = String(lastId);
    let res;
    try {
      res = await fetch(url, { credentials: "include", headers, signal });
    } catch (e) {
      if (signal?.aborted) return;
      if (attempts++ >= maxRetries) { onError?.(e); return; }
      onReconnect?.();
      await new Promise((r) => setTimeout(r, 400 * attempts));
      if (signal?.aborted) return;
      continue;
    }
    // Authorization lost mid-stream: STOP immediately, do NOT retry.
    if (res.status === 401 || res.status === 403) {
      const err = new Error(`stream_http_${res.status}`);
      err.status = res.status;
      onError?.(err);
      return;
    }
    if (!res.ok || !res.body) {
      const err = new Error(`stream_http_${res.status}`);
      err.status = res.status;
      onError?.(err);
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    let gotEof = false;
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) >= 0) {
          const frame = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const parsed = parseFrame(frame);
          if (parsed.event === "eof") { gotEof = true; onEof?.(); }
          else if (parsed.data) {
            try {
              const ev = JSON.parse(parsed.data);
              if (typeof ev.sequence === "number") lastId = ev.sequence;
              onEvent?.(ev);
            } catch { /* ignore malformed frame */ }
          }
        }
      }
    } catch (e) {
      if (signal?.aborted) return;
    }
    if (gotEof || signal?.aborted) return;
    if (attempts++ >= maxRetries) { onError?.(new Error("stream_closed_before_eof")); return; }
    onReconnect?.();
    // Paced resume (same path, same Last-Event-ID contract): a long-lived live
    // tail must survive a brief server blip rather than burn all retries at once.
    await new Promise((r) => setTimeout(r, 400 * attempts));
    if (signal?.aborted) return;
    // loop and resume from lastId (reconnection contract)
  }
}

function parseFrame(frame) {
  const out = { event: null, data: "", id: null };
  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) out.event = line.slice(6).trim();
    else if (line.startsWith("data:")) out.data += line.slice(5).trim();
    else if (line.startsWith("id:")) out.id = line.slice(3).trim();
  }
  return out;
}
